import React, { ReactElement } from 'react';
import { IconRemove } from 'src/content/icons';
import { strings } from 'src/localization';
import css from './ServicesCard.module.scss';
import { ScrollBar } from '@framework-controls/core';
import { Services } from 'src/models';
import classNames from 'classnames';
import { ViewMode } from 'src/enums';

export interface IProps {
    services: Services[];
    removeService: (id: string, e: any) => void;
    totalMinCost: string;
    myCart: boolean;
    skipServiceReason?: string;
    viewMode?: ViewMode;
    theme: string;
    gmRedeemAmt: string | undefined;
    gmDealerCanEarn: string | undefined;
    gmDealerCanRedeem: string | undefined;
    modifyServices?: (e: any) => void;
    noServicesSelected?: (e: any) => void;
    currentMainPane: string;
    displayMCMessage?: boolean;
    highlightNoServicesSelected?: boolean;
}

const ServicesCard = (props: IProps) => {
    let serviceKey = 'Summary';
    if (props.myCart) {
        serviceKey = 'MyCart';
    }
    // PMR 1462985 requested that we always underline the link
    let linkButtonClassName = css.summaryEditLinkADA;
    if (props.theme === 'colorSensitive') {
        linkButtonClassName = css.summaryEditLinkADA;
    }

    const titleClasses = classNames(
        css.summaryTitle,
        {
            [css.marginTop]: (!props.myCart),
        },
        {
            [css.titleMyCart]: (props.myCart),
        },
    );

    const servicesClasses = classNames(
        css.summaryCard,
        {
            [css.menuCard]: (props.myCart),
            [css.menuCardMINI]: (props.theme === 'mini' && props.myCart),
            [css.summaryCardBMW]: (props.theme==='bmw' && !props.myCart),
            [css.summaryCardSPM]: (props.theme==='toyotaSPM'),
            [css.summaryCardLM]: (props.theme==='lexusMonogram'),
            [css.summaryCardCA]: (props.theme==='cadillac'),
        },
    );

    const servicesText = classNames(
        css.serviceText,
        {
            [css.serviceLexusCart]: ((props.theme === 'lexus' || props.theme === 'lexusMonogram') && props.myCart),
            [css.serviceLexusSummary]: ((props.theme === 'lexus' || props.theme === 'lexusMonogram') && !props.myCart),
        },
    );

    const servicesPrice = classNames(
        css.servicePrice,
        {
            [css.serviceLexusCart]: ((props.theme === 'lexus' || props.theme === 'lexusMonogram') && props.myCart),
            [css.serviceLexusSummary]: ((props.theme === 'lexus' || props.theme === 'lexusMonogram') && !props.myCart),
        },
    );

    const servicesIcon = classNames(
        css.icon,
        {
            [css.iconLexus]: ((props.theme === 'lexus' || props.theme === 'lexusMonogram') && props.myCart),
        },
    );

    const gmRewardsNewCost = classNames(
        css.gmRewardsNewCost,
        {
            [css.gmRewardsNewCostAu]: (props.theme === 'audi' && !props.myCart),
        },
    );

    const totalMinCostGM = classNames(
        css.totalMinCostGM,
        {
            [css.totalMinCostGM_Au]: (props.theme === 'audi' && !props.myCart),
        },
    );

    const totalMinCost = classNames(
        css.totalMinCost,
        {
            [css.totalMinCost_Au]: (props.theme === 'audi' && props.myCart),
            [css.totalMinCostSPM]: (props.theme === 'toyotaSPM'),
        },
    );

    let header = (<h4 id='servicesHeader' className={titleClasses}>{!props.myCart ? strings.Services : strings.MyCart}</h4>);

    if (props.viewMode && props.viewMode === ViewMode.EditAppt && props.modifyServices) {
        header = (
            <div id='servicesHeader' style={{ display: 'flex' }}>
                <h4 className={titleClasses}>{!props.myCart ? strings.Services : strings.MyCart}</h4>
                <div onClick={props.modifyServices} className={linkButtonClassName}>{strings.UpdateServices}</div>
            </div>
        );
    }

    if (props.skipServiceReason) {
        return (
            <div className={css.flexItemRight}>
                {header}
                <div className={servicesClasses + ' ' + css.flexBox}>
                    <p>{props.skipServiceReason}</p>
                </div>
            </div>
        );
    }
    let servicesList = props.services.map((service, index) => (
        <div key={index + serviceKey} className={css.service} id={'service' + index}>
            <h6 className={servicesText}>
                {service.serviceName?.length > 225 ? service.serviceName?.substring(0, 225) + '...' :
                    service.serviceName}
            </h6>
            <h6 className={servicesPrice}>{service.range ?? ''}</h6>
            <IconRemove
                id={'deleteBtn' + index}
                onClick={(e: any) => props.removeService(service.id, e)}
                className={servicesIcon}
            />
        </div>
    ));

    if (props.services.length === 0 && props.currentMainPane !== 'AddVehicle') {
        servicesList = [
            <div key={serviceKey}>
                <p key={serviceKey}
                    className={`${css.noServices} ${props.highlightNoServicesSelected ? css.noServicesHighlight : ''}`}>
                    {strings.NoServicesSelected}
                </p>
                <p onClick={props.noServicesSelected} className={css.selectServices}>
                    {strings.NoServicesClickHere}
                </p>
                <p className={`${css.noServices} ${props.highlightNoServicesSelected ? css.noServicesHighlight : ''}`}>
                    {strings.ToSelectAService}
                </p>
            </div>,
        ];
    }
    let totalCost: ReactElement | string = strings.CallDealerForPricing;
    let costAfterRewards: ReactElement | null = null;
    let estimatedRewards: ReactElement | null = null;

    if (props.totalMinCost !== '****') {
        totalCost = <div className={totalMinCost}>{strings.MinEstCost + Number(props.totalMinCost).toFixed(2)}</div>;
        if (props.gmRedeemAmt && parseFloat(props.gmRedeemAmt) > 0 &&
            props.gmDealerCanEarn === 'Y' &&
            props.gmDealerCanRedeem === 'Y') {
            estimatedRewards = (
                <div className={totalMinCostGM}>
                    <div className={css.gmLit}>{strings.EstimatedGMRewards}</div>
                    <div className={css.gmValue}>{strings.DollarGM}{parseFloat(props.gmRedeemAmt).toFixed(2).toString()}</div>
                </div>
            );
            totalCost = (
                <div className={totalMinCostGM}>
                    <div className={css.gmLit}>{strings.MinEstCostGM}</div>
                    <div className={css.gmValue}>{strings.DollarGM}{Number(props.totalMinCost).toFixed(2)}</div>
                </div>
            );

            // calculate the cost after gm rewards are applied.
            let newCost: number = Number(props.totalMinCost) - Number(props.gmRedeemAmt);
            costAfterRewards = (
                <div className={gmRewardsNewCost}>
                    <div className={css.gmLit}>{strings.MinCostAfterRewards}</div>
                    <div className={css.gmValue}>{strings.DollarGM}{newCost.toFixed(2).toString()}</div>
                </div>
            );
            if (newCost < 0) {
                newCost = 0;
                costAfterRewards = (
                    <div className={gmRewardsNewCost}>
                        <div className={css.gmLit}>{strings.MinCostAfterRewards}</div>
                        <div className={css.gmValue}>{strings.DollarGM}{newCost.toFixed(2).toString()}</div>
                    </div>
                );
            }
        }
    } else {
        if (props.gmRedeemAmt && parseFloat(props.gmRedeemAmt) > 0 &&
            props.gmDealerCanEarn === 'Y' &&
            props.gmDealerCanRedeem === 'Y') {
            estimatedRewards = (
                <div className={totalMinCost}>
                    <div className={css.gmLit}>{strings.EstimatedGMRewards}</div>
                    <div className={css.gmValue}>{strings.DollarGM}{parseFloat(props.gmRedeemAmt).toFixed(2).toString()}</div>
                </div>
            );
        }
    }

    let servicesArea: ReactElement | null = <div>{servicesList}</div>;
    if (serviceKey === 'MyCart') {
        servicesArea = <ScrollBar scrollVertical><div className={css.myCartServices}>{servicesList}</div></ScrollBar>;
    }

    const gmRewardsDisclaimer = (!props.myCart && props.gmDealerCanEarn === 'Y' && props.gmDealerCanRedeem === 'Y') ?
        <p>{strings.GMRewardsDisclaimerSummary}</p> : null;

    return (
        <div className={css.flexItemRight}>
            {header}
            <div className={servicesClasses + ' ' + css.flexBox} key={serviceKey}>
                {servicesArea}
            </div>
            <hr className={css.servicesHR}></hr>
            {totalCost}
            {estimatedRewards}
            {costAfterRewards}
            {gmRewardsDisclaimer}
            {props.displayMCMessage ? <div className={css.maintContractMessage}>
                {strings.MaintContractMessage + ' ' + strings.MaintContractSubheading3}
            </div>: null
            }
        </div>
    );
};

export default ServicesCard;
